import { useMemo, useState, useCallback } from "react";

/**
 * @param {Array<any>} data - dane do posortowania
 * @param {Object} [options]
 * @param {string|null} [options.initialKey=null] - początkowa kolumna
 * @param {"asc"|"desc"} [options.initialDirection="asc"]
 * @param {Object<string, Function>} [options.accessors] - np. { count: (row)=>Number(row.count) }
 * @param {Intl.Collator} [options.collator] - do sortowania tekstów (lokalizacja)
 */
export function useSort(data, {
  initialKey = null,
  initialDirection = "asc",
  accessors = {},
  collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })
} = {}) {
  const [sortConfig, setSortConfig] = useState({ key: initialKey, direction: initialDirection });

  const getValue = useCallback((row, key) => {
    if (!key) return undefined;
    const accessor = accessors[key];
    const raw = row?.[key];
    return typeof accessor === "function" ? accessor(row, raw) : raw;
  }, [accessors]);

  const compare = useCallback((a, b) => {
    if (!sortConfig.key) return 0;

    const aVal = getValue(a, sortConfig.key);
    const bVal = getValue(b, sortConfig.key);

    const aNull = (aVal === null || aVal === undefined);
    const bNull = (bVal === null || bVal === undefined);
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;

    let cmp = 0;
    if (typeof aVal === "string" || typeof bVal === "string") {
      cmp = collator.compare(String(aVal), String(bVal));
    } else if (typeof aVal === "number" && typeof bVal === "number") {
      cmp = aVal - bVal;
    } else if (aVal instanceof Date || bVal instanceof Date) {
      cmp = new Date(aVal).getTime() - new Date(bVal).getTime();
    } else {
      cmp = collator.compare(String(aVal), String(bVal));
    }

    return sortConfig.direction === "asc" ? cmp : -cmp;
  }, [sortConfig, getValue, collator]);
  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!sortConfig.key) return data;
    return data
      .map((item, idx) => ({ item, idx }))
      .sort((a, b) => {
        const c = compare(a.item, b.item);
        return c !== 0 ? c : a.idx - b.idx;
      })
      .map(x => x.item);
  }, [data, compare, sortConfig.key]);

  const requestSort = useCallback((key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        const nextDir = prev.direction === "asc" ? "desc" : "asc";
        return { key, direction: nextDir };
      }

      return { key, direction: "asc" };
    });
  }, []);

  const resetSort = useCallback(() => setSortConfig({ key: null, direction: "asc" }), []);

  return { sortConfig, sortedData, requestSort, resetSort, setSortConfig };
}
